import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  getTrustQueue,
  type TrustAction,
  type TrustDecision,
  type TrustLayer,
  type TrustQueueResponse,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'

type SurfaceFilter = '' | 'event' | 'text' | 'image'

const SURFACE_FILTERS: { value: SurfaceFilter; label: string }[] = [
  { value: '', label: 'All surfaces' },
  { value: 'event', label: 'Events' },
  { value: 'text', label: 'Text' },
  { value: 'image', label: 'Images' },
]

const ACTION_STYLES: Record<TrustAction, string> = {
  allow:
    'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  review:
    'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  block: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
}

const LAYER_STYLES: Record<TrustLayer, string> = {
  cache: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  rules: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  embeddings:
    'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200',
  llm: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200',
  rekognition:
    'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-200',
  disabled: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime()
  const diff = Date.now() - t
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`
  return `${Math.floor(diff / 86_400_000)}d ago`
}

export function TrustQueue() {
  const canRead = usePermission('trust', 'read')
  const [items, setItems] = useState<TrustDecision[]>([])
  const [surface, setSurface] = useState<SurfaceFilter>('')
  const [cursor, setCursor] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(
    async (resetCursor = true) => {
      if (!canRead) return
      setLoading(true)
      setError(null)
      try {
        const response: TrustQueueResponse = await getTrustQueue({
          surface: surface || undefined,
          cursor: resetCursor ? undefined : cursor || undefined,
          limit: 50,
        })
        setItems((prev) => (resetCursor ? response.items : [...prev, ...response.items]))
        setNextCursor(response.next_cursor ?? null)
        if (resetCursor) {
          setCursor(null)
        }
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load trust queue')
      } finally {
        setLoading(false)
      }
    },
    [canRead, surface, cursor]
  )

  useEffect(() => {
    load(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surface])

  if (!canRead) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-semibold mb-2">Sentinel</h1>
        <p className="text-gray-600 dark:text-gray-400">
          You don't have permission to view the Sentinel review queue. Ask a super admin to grant you the
          <code className="mx-1 px-1 rounded bg-gray-200 dark:bg-gray-800">trust:read</code>
          permission.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#7f4f9a] dark:text-[#b58fcc]">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
            Sentinel
          </div>
          <h1 className="font-heading text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
            Review Queue
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Content flagged by the trust pipeline awaiting moderator review.
          </p>
        </div>

        <Link
          to="/trust/metrics"
          className="inline-flex items-center gap-2 rounded-xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] px-4 py-2 text-sm font-medium text-[#503864] dark:text-[#d0b8df] hover:bg-[#f9f4ff] dark:hover:bg-[#241b2e] transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
          </svg>
          Metrics
        </Link>
      </div>

      <div className="flex flex-wrap gap-2">
        {SURFACE_FILTERS.map((opt) => (
          <button
            key={opt.value || 'all'}
            onClick={() => setSurface(opt.value)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              surface === opt.value
                ? 'bg-[#503864] dark:bg-[#7f4f9a] text-white border-transparent'
                : 'bg-white dark:bg-[#1a1520] text-gray-700 dark:text-gray-300 border-gray-200 dark:border-[#3d2c4f] hover:bg-[#f9f4ff] dark:hover:bg-[#241b2e]'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/40 p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="rounded-2xl border border-[#ecdefc] dark:border-[#3d2c4f] bg-white dark:bg-[#1a1520] overflow-hidden">
        <div className="hidden md:grid grid-cols-[1fr_100px_100px_120px_1fr_140px] gap-4 px-5 py-3 border-b border-[#f0e3ff] dark:border-[#3d2c4f] bg-[#fbf7ff] dark:bg-[#15111c] text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <div>Decision</div>
          <div>Surface</div>
          <div>Action</div>
          <div>Layer</div>
          <div>Reason</div>
          <div>Created</div>
        </div>

        {loading && items.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 px-6">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#f5edff] dark:bg-[#2a1f3d] mb-3">
              <svg className="w-6 h-6 text-[#7f4f9a]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">Queue is empty</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">No items currently awaiting review.</p>
          </div>
        ) : (
          <ul className="divide-y divide-[#f0e3ff] dark:divide-[#3d2c4f]">
            {items.map((d) => (
              <li key={d.decision_id}>
                <Link
                  to={`/trust/decisions/${d.decision_id}`}
                  className="block px-5 py-4 hover:bg-[#fbf7ff] dark:hover:bg-[#241b2e] transition-colors"
                >
                  <div className="md:grid md:grid-cols-[1fr_100px_100px_120px_1fr_140px] md:gap-4 md:items-center flex flex-col gap-2">
                    <div className="font-mono text-xs text-gray-700 dark:text-gray-300 truncate">
                      {d.decision_id}
                    </div>
                    <div>
                      <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        {d.surface}
                      </span>
                    </div>
                    <div>
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide ${ACTION_STYLES[d.action]}`}>
                        {d.action}
                      </span>
                    </div>
                    <div>
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${LAYER_STYLES[d.layer]}`}>
                        {d.layer}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-300 truncate">
                      {d.reasons[0] ? (
                        <span>
                          <code className="text-xs text-[#7f4f9a] dark:text-[#b58fcc]">{d.reasons[0].code}</code>
                          {d.reasons[0].description && (
                            <span className="text-gray-500 dark:text-gray-400 ml-1">· {d.reasons[0].description}</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400" title={new Date(d.created_at).toLocaleString()}>
                      {relativeTime(d.created_at)}
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>

      {nextCursor && (
        <div className="flex justify-center">
          <CustomButton
            variant="secondary"
            disabled={loading}
            loading={loading}
            onClick={() => {
              setCursor(nextCursor)
              load(false)
            }}
          >
            Load more
          </CustomButton>
        </div>
      )}
    </div>
  )
}
